import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';

import { JBEnvironmentId } from '@bjb-gcrm/gcrm-frontend-components';
import { BrMgmtNavigationInstructionsWrapperWC } from '@juliusbaer-gcrm/pcm-br-mgmt-components/BrMgmtNavigationInstructionsWrapperWC';
import { ZfBrRelationsNavigationInstructionsWrapperWC } from '@juliusbaer-gcrm/pcm-br-mgmt-components/ZfBrRelationsNavigationInstructionsWrapperWC';
import { ZfServicingRolesNavigationInstructionsWrapperWC } from '@juliusbaer-gcrm/pcm-br-mgmt-components/ZfServicingRolesNavigationInstructionsWrapperWC';
import { DeliveryInstructionsNavigationInstructionsWrapperWC } from '@juliusbaer-gcrm/pcm-br-mgmt-components/DeliveryInstructionsNavigationInstructionsWrapperWC';
import { BlockingInstructionsNavigationInstructionsWrapperWC } from '@juliusbaer-gcrm/pcm-br-mgmt-components/BlockingInstructionsNavigationInstructionsWrapperWC';
import { PcoNavigationInstructionsWrapperWC } from '@juliusbaer-gcrm/pco-product-service-components';
import { BrPurposeNavigationInstructionsWizard } from 'views/NavigationWizard/components/BrPurposeNavigationInstructionsWizard';
import { GlobalState } from 'reducers/stateTypes';
import { BrTabItems } from 'views/BrTabs/BrTabs';

type Props = {
  env: JBEnvironmentId;
};

const noop = () => undefined;

const wrapperProps = (env: JBEnvironmentId) => ({
  env,
  version: '0.0.1',
  isActive: true,
  onSectionSelect: noop,
});

const renderInstructions = (tab: BrTabItems, env: JBEnvironmentId): ReactNode => {
  switch (tab) {
    case BrTabItems.CORE_DATA:
      return <BrMgmtNavigationInstructionsWrapperWC {...wrapperProps(env)} />;
    case BrTabItems.BR_RELATIONS:
      return <ZfBrRelationsNavigationInstructionsWrapperWC {...wrapperProps(env)} />;
    case BrTabItems.BR_SERVICING:
      return <ZfServicingRolesNavigationInstructionsWrapperWC {...wrapperProps(env)} />;
    case BrTabItems.BR_PURPOSE:
      return <BrPurposeNavigationInstructionsWizard />;
    case BrTabItems.BR_DELIVERY_INSTRUCTIONS:
      return <DeliveryInstructionsNavigationInstructionsWrapperWC {...wrapperProps(env)} />;
    case BrTabItems.BLOCKING_INSTRUCTIONS:
      return <BlockingInstructionsNavigationInstructionsWrapperWC {...wrapperProps(env)} />;
    case BrTabItems.PRODUCTS_AND_SERVICES:
      return <PcoNavigationInstructionsWrapperWC {...wrapperProps(env)} />;
    default:
      return null;
  }
};

export const InstructionsTab = ({ env }: Props) => {
  const { t } = useTranslation();
  const selectedTab = useSelector((state: GlobalState) => state.brTabs.tab);
  const instructions = renderInstructions(selectedTab, env);

  if (!instructions) {
    return <p>{t('navigationWizard.instructions.empty')}</p>;
  }

  return (
    <ErrorBoundary key={selectedTab} fallback={<p>{t('navigationWizard.instructions.error')}</p>}>
      {instructions}
    </ErrorBoundary>
  );
};
